/**
 * When the balance is coming.
 *
 * A deal that is part paid is the easiest thing on the lot to lose. Five
 * thousand down, "I'll bring the rest Friday", and the only record of Friday
 * is in somebody's head. This records it.
 *
 * One row per promise. A date that gets pushed produces a second row rather
 * than overwriting the first, because a customer who has moved the date three
 * times is a different customer from one who has moved it once and the
 * difference is worth being able to see. See 33_payment_promises.sql.
 *
 * Nothing here decides what is owed. The debt is computed from the payments
 * actually received, in deal_balances, so a promise against a deal that has
 * since been settled drops out of open_promises on its own.
 */

import { supabase } from "../../lib/supabase";

export type PromiseStatus = "open" | "kept" | "rescheduled" | "cancelled";

/** A row of open_promises: the promise with the live balance attached. */
export interface OpenPromise {
  id: string;
  deal_id: string;
  promised_for: string;
  promised_cents: number | null;
  note: string | null;
  remind_customer: boolean;
  remind_days_before: number;
  reminded_at: string | null;
  reminder_error: string | null;
  created_at: string;
  owing_cents: number;
  chase_cents: number;
  overdue: boolean;
  days_away: number;
  deal_no: string;
  deal_status: string;
  customer_id: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  vehicle: string | null;
  stock_no: string | null;
}

/** A row of the table itself, for the history list on a deal. */
export interface PromiseRow {
  id: string;
  promised_for: string;
  amount_cents: number | null;
  note: string | null;
  status: PromiseStatus;
  settled_on: string | null;
  outcome_note: string | null;
  remind_customer: boolean;
  remind_days_before: number;
  reminded_at: string | null;
  reminder_error: string | null;
  created_at: string;
}

export const STATUS_LABEL: Record<PromiseStatus, string> = {
  open: "Waiting",
  kept: "Paid",
  rescheduled: "Date moved",
  cancelled: "Cancelled",
};

/* The dealership's day. toISOString is UTC, so between 8pm and midnight
   Eastern it returns tomorrow — which stamped tomorrow's date on anything
   settled in the evening and made a promise due today read as a day late. */
export const today = () =>
  new Date().toLocaleDateString("en-CA", { timeZone: "America/New_York" });

/* ------------------------------------------------------------------ */
/* Reading                                                             */
/* ------------------------------------------------------------------ */

/** Every promise ever made on this deal, newest first. */
export async function promiseHistory(dealId: string): Promise<PromiseRow[]> {
  const { data, error } = await supabase
    .from("payment_promises")
    .select("id,promised_for,amount_cents,note,status,settled_on,outcome_note,remind_customer,remind_days_before,reminded_at,reminder_error,created_at")
    .eq("deal_id", dealId)
    .order("created_at", { ascending: false });
  if (error) throw new Error(`Could not load the promises: ${error.message}`);
  return (data ?? []) as PromiseRow[];
}

/** The one that is still waiting, if there is one. */
export async function openPromiseFor(dealId: string): Promise<OpenPromise | null> {
  const { data, error } = await supabase
    .from("open_promises").select("*").eq("deal_id", dealId).maybeSingle();
  if (error) throw new Error(`Could not load the promise: ${error.message}`);
  return (data as OpenPromise) ?? null;
}

/** Everything outstanding across the lot, soonest first. */
export async function allOpenPromises(): Promise<OpenPromise[]> {
  const { data, error } = await supabase
    .from("open_promises").select("*").order("promised_for");
  if (error) throw new Error(`Could not load balances due: ${error.message}`);
  return (data ?? []) as OpenPromise[];
}

/* ------------------------------------------------------------------ */
/* Writing                                                             */
/* ------------------------------------------------------------------ */

export interface NewPromise {
  dealId: string;
  /** ISO yyyy-mm-dd. */
  promisedFor: string;
  /** Null means the whole balance on the day. */
  amountCents: number | null;
  note: string | null;
  remindCustomer: boolean;
  remindDaysBefore: number;
}

/**
 * Record what they said.
 *
 * Any promise already open is marked "date moved" first, because the database
 * allows only one live promise per deal — two open promises for the same money
 * is a state nobody can act on. The old row stays, which is the point.
 */
export async function makePromise(p: NewPromise): Promise<PromiseRow> {
  if (!p.promisedFor) throw new Error("Pick the date the balance is coming.");
  if (p.promisedFor < today()) {
    /* A date already gone cannot be promised, and more to the point no
       reminder would ever be sent for it — the worker only mails between the
       lead-in day and the date itself. Saving it would have printed a
       confirmation claiming an email that was never going to go. */
    throw new Error(
      "That date has already passed. Put the date they are actually bringing "
      + "it, or use Stop chasing if they are not.");
  }
  if (p.amountCents !== null && p.amountCents <= 0) {
    throw new Error("The amount has to be more than zero, or left blank for the whole balance.");
  }
  if (!Number.isInteger(p.remindDaysBefore)
      || p.remindDaysBefore < 0 || p.remindDaysBefore > 30) {
    throw new Error("Remind between 0 and 30 days before.");
  }

  /* One statement, one transaction. As two round trips from the browser, a
     failure between them closed the old promise with "A new date was agreed"
     against it and saved no new date — a history entry recording something
     that never happened, in the one table built so the history could be
     trusted. It also raced two tabs into a unique-violation. */
  const { data, error } = await supabase.rpc("record_payment_promise", {
    p_deal_id: p.dealId,
    p_promised_for: p.promisedFor,
    p_amount_cents: p.amountCents,
    p_note: p.note ?? null,
    p_remind: p.remindCustomer,
    p_days_before: p.remindDaysBefore,
  });

  if (error || !data) {
    throw new Error(
      `Could not save the date: ${error?.message ?? "nothing came back"}. `
      + "Nothing was changed — any earlier date is still on the deal.");
  }
  return data as PromiseRow;
}

/** Stop chasing, without pretending it was paid. */
export async function cancelPromise(id: string, why: string): Promise<void> {
  const { data, error } = await supabase
    .from("payment_promises")
    .update({ status: "cancelled", settled_on: today(),
              outcome_note: why.trim() || null })
    .eq("id", id).eq("status", "open")
    .select("id");
  if (error) throw new Error(`Could not cancel it: ${error.message}`);
  if (!data?.length) {
    throw new Error("That promise is no longer open — reload to see where it got to.");
  }
}

/**
 * Close the open promise once the money is in.
 *
 * Called after a payment lands. Best effort on purpose: the payment is the
 * thing that matters and it is already recorded, so a failure here must not
 * be allowed to look like the payment failed. The promise simply drops out of
 * open_promises anyway, because that view requires a balance.
 */
export async function markKeptIfSettled(dealId: string): Promise<void> {
  try {
    const { data: bal } = await supabase
      .from("deal_balances").select("owing_cents").eq("id", dealId).maybeSingle();
    if (!bal || Number(bal.owing_cents) > 0) return;
    await supabase
      .from("payment_promises")
      .update({ status: "kept", settled_on: today(),
                outcome_note: "The balance was paid." })
      .eq("deal_id", dealId).eq("status", "open");
  } catch (e) {
    console.warn("promise not closed after payment", e);
  }
}
